/**
 * Utility functions for handling organization paths
 */

/**
 * Prepends the organization slug to a path
 * @param orgSlug The organization slug
 * @param path The path to prepend the slug to
 * @returns The path with the organization slug prepended
 */
export function getOrgPath(orgSlug: string, path: string): string {
  // Special routes that should not have the organization slug prepended
  const globalRoutes = [
    '/organizations', 
    '/account',
    '/organization-setup',
    '/user-setup',
    '/client-portal'
  ];
  
  // Check if this is a special route
  for (const route of globalRoutes) {
    if (path === route || path.startsWith(`${route}/`)) {
      return path;
    }
  }
  
  // Handle the today path specially - today is under organization
  if (path === '/today' || path === '/') {
    return `/${orgSlug}/today`;
  }
  
  // If the path already starts with a slash, remove it
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  return `/${orgSlug}/${cleanPath}`;
}

/**
 * Extracts the organization slug from a path
 * @param path The path to extract the slug from
 * @returns The organization slug, or null if not found
 */
export function extractOrgSlug(path: string | null | undefined): string | null {
  if (!path) return null;
  
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  
  // Split the path by slashes
  const parts = cleanPath.split('/');
  
  // Check if first part is a special route
  const specialRoutes = [
    'account',
    'organizations', 
    'organization-setup',
    'user-setup',
    'client-portal'
  ];
  
  const firstPart = parts[0];
  if (firstPart && specialRoutes.includes(firstPart)) {
    return null;
  }
  
  // The first part should be the organization slug
  if (parts.length > 0 && parts[0]) {
    return parts[0];
  }
  return null;
} 
